import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { adminBenchmarkService, Benchmark, BenchmarkStatistics, TimeSeriesResponse } from '@/services/adminBenchmarkService';
import { toastHelper } from '@/utils/toastHelper';
import { RefreshCw, Eye } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const BenchmarkDashboard: React.FC = () => {
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<BenchmarkStatistics | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesResponse | null>(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState<Benchmark | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;

  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minFFmpegTime, setMinFFmpegTime] = useState('');
  const [maxFFmpegTime, setMaxFFmpegTime] = useState('');
  const [minUploadTime, setMinUploadTime] = useState('');
  const [maxUploadTime, setMaxUploadTime] = useState('');
  const [minTotalTime, setMinTotalTime] = useState('');
  const [maxTotalTime, setMaxTotalTime] = useState('');
  const [aggregation, setAggregation] = useState<'daily' | 'weekly' | 'monthly'>('weekly');

  const fetchBenchmarks = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        limit: pageSize,
        offset: currentPage * pageSize
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (minFFmpegTime) params.min_ffmpeg_time = parseFloat(minFFmpegTime);
      if (maxFFmpegTime) params.max_ffmpeg_time = parseFloat(maxFFmpegTime);
      if (minUploadTime) params.min_upload_time = parseFloat(minUploadTime);
      if (maxUploadTime) params.max_upload_time = parseFloat(maxUploadTime);
      if (minTotalTime) params.min_total_time = parseFloat(minTotalTime);
      if (maxTotalTime) params.max_total_time = parseFloat(maxTotalTime);

      const data = await adminBenchmarkService.getBenchmarks(params);
      setBenchmarks(data.benchmarks);
      setTotalCount(data.total);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch benchmarks');
    } finally {
      setLoading(false);
    }
  }, [currentPage, startDate, endDate, minFFmpegTime, maxFFmpegTime, minUploadTime, maxUploadTime, minTotalTime, maxTotalTime]);

  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await adminBenchmarkService.getBenchmarkStatistics(
        startDate || undefined,
        endDate || undefined
      );
      setStatistics(stats);
    } catch (error: any) {
      console.error('Failed to fetch statistics:', error);
    }
  }, [startDate, endDate]);

  const fetchTimeSeriesData = useCallback(async () => {
    // We'll use the benchmarks data directly for the chart instead of aggregated time-series
    // This is handled by using benchmarks state
  }, []);

  useEffect(() => {
    fetchBenchmarks();
    fetchStatistics();
    fetchTimeSeriesData();
  }, [fetchBenchmarks, fetchStatistics, fetchTimeSeriesData]);

  useEffect(() => {
    // Debounce filter changes
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchBenchmarks();
      fetchStatistics();
      fetchTimeSeriesData();
    }, 300);
    return () => clearTimeout(timer);
  }, [startDate, endDate, minFFmpegTime, maxFFmpegTime, minUploadTime, maxUploadTime, minTotalTime, maxTotalTime]);

  const handleViewBenchmark = (benchmark: Benchmark) => {
    setSelectedBenchmark(benchmark);
    setIsDetailModalOpen(true);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setMinFFmpegTime('');
    setMaxFFmpegTime('');
    setMinUploadTime('');
    setMaxUploadTime('');
    setMinTotalTime('');
    setMaxTotalTime('');
    setCurrentPage(0);
  };

  const formatTime = (seconds: number | null) => {
    if (seconds === null) return 'N/A';
    return `${seconds.toFixed(2)}s`;
  };

  const formatSize = (bytes: number | null) => {
    if (bytes === null) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Aggregate benchmarks based on selected period
  const getAggregatedData = () => {
    if (benchmarks.length === 0) return [];

    // For daily aggregation, show individual points
    if (aggregation === 'daily') {
      return benchmarks.map(benchmark => ({
        timestamp: new Date(benchmark.created_at).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        }),
        'FFmpeg Time': benchmark.ffmpeg_time,
        'Upload Time': benchmark.upload_time,
        'Total Time': benchmark.total_time,
        count: 1
      })).reverse();
    }

    // For weekly/monthly, aggregate data
    const grouped: { [key: string]: { ffmpeg: number[], upload: number[], total: number[] } } = {};

    benchmarks.forEach(benchmark => {
      const date = new Date(benchmark.created_at);
      let key: string;

      if (aggregation === 'weekly') {
        // Get week start (Sunday)
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        // Monthly
        key = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }

      if (!grouped[key]) {
        grouped[key] = { ffmpeg: [], upload: [], total: [] };
      }

      if (benchmark.ffmpeg_time) grouped[key].ffmpeg.push(benchmark.ffmpeg_time);
      if (benchmark.upload_time) grouped[key].upload.push(benchmark.upload_time);
      if (benchmark.total_time) grouped[key].total.push(benchmark.total_time);
    });

    // Calculate averages
    return Object.entries(grouped).map(([key, values]) => ({
      timestamp: key,
      'FFmpeg Time': values.ffmpeg.length > 0
        ? values.ffmpeg.reduce((a, b) => a + b, 0) / values.ffmpeg.length
        : null,
      'Upload Time': values.upload.length > 0
        ? values.upload.reduce((a, b) => a + b, 0) / values.upload.length
        : null,
      'Total Time': values.total.length > 0
        ? values.total.reduce((a, b) => a + b, 0) / values.total.length
        : null,
      count: values.ffmpeg.length
    })).reverse();
  };

  const chartData = getAggregatedData();

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-none">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Benchmark Dashboard</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Monitor video processing performance metrics</p>
        </div>
        <Button onClick={() => { fetchBenchmarks(); fetchStatistics(); fetchTimeSeriesData(); }} variant="outline" size="sm" className="self-start sm:self-auto">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Benchmarks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.total_benchmarks}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg FFmpeg Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{formatTime(statistics.avg_ffmpeg_time)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Range: {formatTime(statistics.min_ffmpeg_time)} - {formatTime(statistics.max_ffmpeg_time)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg Upload Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{formatTime(statistics.avg_upload_time)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Range: {formatTime(statistics.min_upload_time)} - {formatTime(statistics.max_upload_time)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avg Total Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{formatTime(statistics.avg_total_time)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Range: {formatTime(statistics.min_total_time)} - {formatTime(statistics.max_total_time)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Time-Series Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Performance Trends</CardTitle>
              <CardDescription>
                {aggregation === 'daily' ? 'Individual benchmarks' : `Average per ${aggregation === 'weekly' ? 'week' : 'month'}`}
                {' '}(showing up to 50 most recent)
              </CardDescription>
            </div>
            <Select value={aggregation} onValueChange={(value) => setAggregation(value as any)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  angle={aggregation === 'daily' ? -45 : 0}
                  textAnchor={aggregation === 'daily' ? "end" : "middle"}
                  height={aggregation === 'daily' ? 80 : 60}
                  interval="preserveStartEnd"
                  style={{ fontSize: aggregation === 'daily' ? '10px' : '12px' }}
                />
                <YAxis label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="FFmpeg Time" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="Upload Time" stroke="#22c55e" strokeWidth={2} />
                <Line type="monotone" dataKey="Total Time" stroke="#a855f7" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-muted-foreground">No time-series data available</div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter benchmarks by date and performance metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="min-ffmpeg">Min FFmpeg Time (s)</Label>
              <Input
                id="min-ffmpeg"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={minFFmpegTime}
                onChange={(e) => setMinFFmpegTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-ffmpeg">Max FFmpeg Time (s)</Label>
              <Input
                id="max-ffmpeg"
                type="number"
                step="0.01"
                placeholder="100.00"
                value={maxFFmpegTime}
                onChange={(e) => setMaxFFmpegTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="min-upload">Min Upload Time (s)</Label>
              <Input
                id="min-upload"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={minUploadTime}
                onChange={(e) => setMinUploadTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-upload">Max Upload Time (s)</Label>
              <Input
                id="max-upload"
                type="number"
                step="0.01"
                placeholder="100.00"
                value={maxUploadTime}
                onChange={(e) => setMaxUploadTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="min-total">Min Total Time (s)</Label>
              <Input
                id="min-total"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={minTotalTime}
                onChange={(e) => setMinTotalTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-total">Max Total Time (s)</Label>
              <Input
                id="max-total"
                type="number"
                step="0.01"
                placeholder="100.00"
                value={maxTotalTime}
                onChange={(e) => setMaxTotalTime(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={clearFilters} variant="outline" size="sm">
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Benchmarks */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Benchmark Records</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Showing {benchmarks.length} of {totalCount} benchmarks
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : benchmarks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No benchmarks found</div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3 p-3">
                {benchmarks.map((benchmark) => (
                  <Card key={benchmark.id} className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            {benchmark.youtube_id || 'N/A'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatTimestamp(benchmark.created_at)}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewBenchmark(benchmark)}
                          className="h-8 shrink-0 text-xs"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Details
                        </Button>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-2">
                        <div>
                          <p className="text-xs text-muted-foreground">FFmpeg</p>
                          <p className="text-sm font-semibold text-blue-600">
                            {formatTime(benchmark.ffmpeg_time)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Upload</p>
                          <p className="text-sm font-semibold text-green-600">
                            {formatTime(benchmark.upload_time)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total</p>
                          <p className="text-sm font-semibold text-purple-600">
                            {formatTime(benchmark.total_time)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-1 border-t">
                        <div>
                          <p className="text-xs text-muted-foreground">Audio</p>
                          <p className="text-xs">{formatSize(benchmark.input_audio_size)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Image</p>
                          <p className="text-xs">{formatSize(benchmark.input_image_size)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Video</p>
                          <p className="text-xs">{formatSize(benchmark.output_video_size)}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block w-full overflow-x-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="px-3">Created At</TableHead>
                      <TableHead className="px-3">YouTube ID</TableHead>
                      <TableHead className="px-3 text-right">FFmpeg Time</TableHead>
                      <TableHead className="px-3 text-right">Upload Time</TableHead>
                      <TableHead className="px-3 text-right">Total Time</TableHead>
                      <TableHead className="px-3 text-right">Input Audio</TableHead>
                      <TableHead className="px-3 text-right">Input Image</TableHead>
                      <TableHead className="px-3 text-right">Output Video</TableHead>
                      <TableHead className="px-3">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {benchmarks.map((benchmark) => (
                      <TableRow key={benchmark.id}>
                        <TableCell className="text-sm px-3 whitespace-nowrap">
                          {formatTimestamp(benchmark.created_at)}
                        </TableCell>
                        <TableCell className="text-sm px-3 font-mono">
                          {benchmark.youtube_id || 'N/A'}
                        </TableCell>
                        <TableCell className="text-sm px-3 text-right">
                          {formatTime(benchmark.ffmpeg_time)}
                        </TableCell>
                        <TableCell className="text-sm px-3 text-right">
                          {formatTime(benchmark.upload_time)}
                        </TableCell>
                        <TableCell className="text-sm px-3 text-right font-semibold">
                          {formatTime(benchmark.total_time)}
                        </TableCell>
                        <TableCell className="text-sm px-3 text-right">
                          {formatSize(benchmark.input_audio_size)}
                        </TableCell>
                        <TableCell className="text-sm px-3 text-right">
                          {formatSize(benchmark.input_image_size)}
                        </TableCell>
                        <TableCell className="text-sm px-3 text-right">
                          {formatSize(benchmark.output_video_size)}
                        </TableCell>
                        <TableCell className="px-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewBenchmark(benchmark)}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4">
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                      className="h-8 text-xs sm:text-sm"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="h-8 text-xs sm:text-sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Benchmark Details</DialogTitle>
            <DialogDescription>
              Detailed performance metrics for this upload
            </DialogDescription>
          </DialogHeader>
          {selectedBenchmark && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Benchmark ID</Label>
                  <p className="text-sm font-mono">{selectedBenchmark.id}</p>
                </div>
                <div>
                  <Label>User ID</Label>
                  <p className="text-sm font-mono">{selectedBenchmark.user_id}</p>
                </div>
                <div>
                  <Label>YouTube ID</Label>
                  <p className="text-sm font-mono">{selectedBenchmark.youtube_id || 'N/A'}</p>
                </div>
                <div>
                  <Label>Created At</Label>
                  <p className="text-sm">{formatTimestamp(selectedBenchmark.created_at)}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Timing Breakdown</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Decode Time</Label>
                    <p className="text-sm">{formatTime(selectedBenchmark.decode_time)}</p>
                  </div>
                  <div>
                    <Label>Probe Time</Label>
                    <p className="text-sm">{formatTime(selectedBenchmark.probe_time)}</p>
                  </div>
                  <div>
                    <Label>Write Time</Label>
                    <p className="text-sm">{formatTime(selectedBenchmark.write_time)}</p>
                  </div>
                  <div>
                    <Label>FFmpeg Time</Label>
                    <p className="text-sm font-semibold text-blue-600">{formatTime(selectedBenchmark.ffmpeg_time)}</p>
                  </div>
                  <div>
                    <Label>Read Time</Label>
                    <p className="text-sm">{formatTime(selectedBenchmark.read_time)}</p>
                  </div>
                  <div>
                    <Label>Upload Time</Label>
                    <p className="text-sm font-semibold text-green-600">{formatTime(selectedBenchmark.upload_time)}</p>
                  </div>
                  <div className="col-span-2">
                    <Label>Total Time</Label>
                    <p className="text-xl font-bold text-purple-600">{formatTime(selectedBenchmark.total_time)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">File Sizes</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Input Audio</Label>
                    <p className="text-sm">{formatSize(selectedBenchmark.input_audio_size)}</p>
                  </div>
                  <div>
                    <Label>Input Image</Label>
                    <p className="text-sm">{formatSize(selectedBenchmark.input_image_size)}</p>
                  </div>
                  <div>
                    <Label>Output Video</Label>
                    <p className="text-sm font-semibold">{formatSize(selectedBenchmark.output_video_size)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BenchmarkDashboard;
